// « N sans l'app · partager sur WhatsApp » (D-103) : une annonce ou une convocation qui a des
// destinataires pas encore dans l'app (pas de compte, ou un compte jamais ouvert). Le message tout
// prêt (quoi, quand, le lien de l'app du club et, pour ceux sans compte, celui de l'équipe : ils se
// présentent, le coach valide). Rien ne part tout seul : c'est la personne qui partage.
import './reach.css';
import { teamInviteLink } from '../lib/api';
import { announcementUnreached, eventUnreached, type EventReach, type Unreached } from '../lib/reach-api';
import { linksFor } from '../lib/club-links';
import { supabase } from '../lib/supabase';
import { announcementText, convocationText, toFamilies } from '../domain/access';
import { agree } from '../domain/labels';
import type { Ctx } from './context';
import { html } from './dom';
import { openShare } from './share';
import { someNames } from './send-access';

const names = (people: Unreached[]) => someNames(people.map(p => p.first_name), 8);
const sansApp = (n: number) => `${n} sans l’app`;

/** Le lien de l'équipe quand le message vise une seule équipe que j'encadre et que certains n'ont pas de compte. */
async function teamLinkFor(teamId: string | null | undefined, people: Unreached[], invite: (t: string) => string, staff: boolean): Promise<string | null> {
  if (!teamId || !staff || !people.some(p => !p.account)) return null;
  try { return invite((await teamInviteLink(teamId)).token); } catch { return null; }
}

/** Après l'envoi d'une annonce : ceux qu'elle ne touche pas, et le message à partager. */
export async function offerAnnouncementReach(ctx: Ctx, clubId: string, audience: unknown, title: string, body: string | null): Promise<void> {
  const people = await announcementUnreached(clubId, audience).catch(() => [] as Unreached[]);
  if (!people.length) return;
  const links = await linksFor(clubId);
  const teams = [...new Set(people.map(p => p.team_id).filter(Boolean))] as string[];
  const one = teams.length === 1 ? teams[0]! : null;
  const teamUrl = await teamLinkFor(one, people, links.invite, !!one && ctx.full.staffOf.has(one));
  const club = ctx.clubs.find(c => c.id === clubId)?.name ?? ctx.club?.name ?? 'club';
  const n = people.length;
  openShare(`${sansApp(n)} · partager sur WhatsApp`, html`
    <p class="rc-intro"><strong>Annonce envoyée.</strong> ${n > 1 ? `${n} personnes de ce public n’ont` : 'Une personne de ce public n’a'} pas encore l’app : ${names(people)}.
      Partage-la dans le groupe WhatsApp${one ? ' de l’équipe' : ''}.</p>`,
    announcementText({ club, title, body, appUrl: links.app, teamUrl, families: toFamilies(people) }),
    { footer: html`<p class="hint">${teamUrl ? `Le lien de l’équipe : qui n’a pas encore de compte se présente, ${one && ctx.full.staffOf.has(one) ? 'tu valides' : 'le coach valide'}, et retrouve sa fiche. ` : ''}Un lien personnel ne se poste jamais dans un groupe.</p>` });
}

/** La convocation d'un match : combien n'ont pas l'app, et de quoi les prévenir. */
export async function convocationReach(eventId: string, date: string): Promise<{ count: number; label: string; open: () => Promise<void> } | null> {
  const r = await eventUnreached(eventId, date).catch(() => null as EventReach | null);
  if (!r || !r.people.length) return null;
  return {
    count: r.people.length,
    label: sansApp(r.people.length),
    open: async () => {
      const clubId = supabase ? ((await supabase.from('teams').select('club_id').eq('id', r.team_id).maybeSingle()).data as { club_id: string } | null)?.club_id : null;
      const links = await linksFor(clubId);
      const teamUrl = await teamLinkFor(r.team_id, r.people, links.invite, true);
      openShare(`${sansApp(r.people.length)} · partager sur WhatsApp`, html`
        <p class="rc-intro">${r.people.length > 1 ? `${r.people.length} ${agree(r.gender, 'convoqués', 'convoquées')} n’ont` : 'Une personne convoquée n’a'} pas encore l’app : ${names(r.people)}.
          ${r.people.length > 1 ? agree(r.gender, 'Ils', 'Elles') : 'Elle'} ne ${r.people.length > 1 ? 'reçoivent' : 'reçoit'} pas la convocation : partage-la dans le groupe de l’équipe.</p>`,
        convocationText({ team: r.team, opponent: r.opponent, isHome: r.is_home, title: r.title, day: r.day, time: r.time, location: r.location,
          appUrl: links.app, teamUrl, families: toFamilies(r.people) }),
        { footer: html`<p class="hint">${teamUrl ? 'Le lien de l’équipe : qui n’a pas encore de compte se présente, tu valides, et retrouve sa fiche. ' : ''}Un lien personnel ne se poste jamais dans un groupe.</p>` });
    },
  };
}
